/**
 * UploadsManager — F-32 component for browsing/uploading construction docs.
 *
 * Used standalone (Documents sub-tab) to manage non-progress files: plans, reports, etc.
 * Reuses FileUploader for upload UX and AuthImg for thumbnails of image uploads.
 *
 * Props:
 *   - villaId: string                 — required, scopes the manager
 *   - villas?: [{villa_id, name}]     — optional list to render a villa picker
 *   - onVillaChange?: (id) => void
 *   - canDelete?: boolean             — show delete buttons on each row (default true)
 *
 * UX: filters by doc_type and phase, list with thumbnail + metadata + delete.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import FileUploader from "@/components/ui/file-uploader";
import AuthImg from "@/components/constructor/AuthImg";
import { Trash, FileText, Image as ImageIcon } from "@phosphor-icons/react";

const DOC_TYPES = [
  { value: "photo", label: "Foto" },
  { value: "plan", label: "Plano" },
  { value: "report", label: "Reporte" },
  { value: "other", label: "Otro" },
];

const PHASES = [
  { value: "", label: "Sin fase" },
  { value: "foundation", label: "Cimientos" },
  { value: "structure", label: "Estructura" },
  { value: "finishes", label: "Acabados" },
  { value: "delivery", label: "Entrega" },
];

function humanSize(b) {
  if (!b && b !== 0) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short" });
  } catch { return ""; }
}

export default function UploadsManager({ villaId, villas = [], onVillaChange, canDelete = true }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [filterPhase, setFilterPhase] = useState("all");

  // Upload form state
  const [docType, setDocType] = useState("plan");
  const [phase, setPhase] = useState("");
  const [caption, setCaption] = useState("");
  const [takenAt, setTakenAt] = useState("");
  const [staged, setStaged] = useState(null); // {base64, mime, filename, size}
  const [uploadKey, setUploadKey] = useState(0); // remount FileUploader to clear it
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const refresh = async () => {
    if (!villaId) { setItems([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/construction/uploads?villa_id=${villaId}`);
      setItems(data);
    } catch (e) {
      setMsg(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [villaId]);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (filterType !== "all" && it.doc_type !== filterType) return false;
      if (filterPhase !== "all" && (it.phase || "") !== filterPhase) return false;
      return true;
    });
  }, [items, filterType, filterPhase]);

  const submit = async (e) => {
    e.preventDefault();
    setMsg("");
    if (!staged) { setMsg("Selecciona un archivo primero."); return; }
    setBusy(true);
    try {
      await api.post("/construction/uploads", {
        villa_id: villaId,
        doc_type: docType,
        phase: phase || null,
        caption: caption || null,
        file: { filename: staged.filename, mime: staged.mime, base64: staged.base64 },
        ...(takenAt ? { taken_at: takenAt } : {}),
      });
      setMsg("✓ Documento subido.");
      setCaption("");
      setTakenAt("");
      setStaged(null);
      setUploadKey((k) => k + 1);
      await refresh();
    } catch (e) {
      setMsg(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (fileId) => {
    if (!window.confirm("¿Eliminar este documento?")) return;
    try {
      await api.delete(`/construction/uploads/${fileId}`);
      setItems((prev) => prev.filter((x) => x.file_id !== fileId));
    } catch (e) {
      setMsg(formatError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="uploads-manager">
      {/* Villa picker (if multiple villas provided) */}
      {villas.length >= 2 && (
        <div>
          <label className="text-xs uppercase tracking-widest text-stone-500">Villa</label>
          <select
            className="dv-input mt-1"
            value={villaId || ""}
            onChange={(e) => onVillaChange && onVillaChange(e.target.value)}
            data-testid="uploads-villa-picker"
          >
            {villas.map((v) => <option key={v.villa_id} value={v.villa_id}>{v.name}</option>)}
          </select>
        </div>
      )}

      {/* Upload form */}
      <form onSubmit={submit} className="dv-card space-y-4" data-testid="uploads-form">
        <div className="font-serif-dv text-xl">Subir documento</div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-stone-500">Tipo</label>
            <select
              className="dv-input mt-1"
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              data-testid="uploads-doc-type"
            >
              {DOC_TYPES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-stone-500">Fase (opcional)</label>
            <select
              className="dv-input mt-1"
              value={phase}
              onChange={(e) => setPhase(e.target.value)}
              data-testid="uploads-phase"
            >
              {PHASES.map((p) => <option key={p.value || "none"} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-stone-500">Descripción corta (opcional)</label>
            <input
              type="text"
              className="dv-input mt-1"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Ej. Plano cimentación rev. B"
              data-testid="uploads-caption"
            />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-stone-500">Fecha del avance (opcional)</label>
          <input
            type="date"
            className="dv-input mt-1"
            value={takenAt}
            onChange={(e) => setTakenAt(e.target.value)}
            data-testid="uploads-taken-at"
          />
          <div className="text-[11px] text-stone-500 mt-1">
            Si dejas vacío se usa la fecha de subida.
          </div>
        </div>
        <div>
          <FileUploader
            key={uploadKey}
            onSelect={setStaged}
            onClear={() => setStaged(null)}
            label="Seleccionar imagen o PDF"
            testId="uploads-file"
          />
        </div>
        {msg && <div className="text-sm text-stone-600" data-testid="uploads-msg">{msg}</div>}
        <button
          type="submit"
          disabled={busy || !staged}
          className="dv-btn text-white disabled:opacity-50"
          style={{ background: "#9C5236" }}
          data-testid="uploads-submit"
        >
          {busy ? "Subiendo…" : "Subir documento"}
        </button>
      </form>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="text-stone-500 text-xs uppercase tracking-widest">Filtrar</span>
        <select
          className="dv-input h-9 py-0"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          data-testid="uploads-filter-type"
        >
          <option value="all">Todos los tipos</option>
          {DOC_TYPES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
        </select>
        <select
          className="dv-input h-9 py-0"
          value={filterPhase}
          onChange={(e) => setFilterPhase(e.target.value)}
          data-testid="uploads-filter-phase"
        >
          <option value="all">Todas las fases</option>
          {PHASES.filter((p) => p.value).map((p) => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </select>
        <span className="ml-auto text-stone-500 text-xs">
          {filtered.length} {filtered.length === 1 ? "archivo" : "archivos"}
        </span>
      </div>

      {/* List */}
      <div data-testid="uploads-list">
        {loading ? (
          <div className="text-center text-stone-500 py-8 text-sm">Cargando…</div>
        ) : filtered.length === 0 ? (
          <div className="dv-card text-center text-stone-500 py-10 text-sm">
            Sin documentos para los filtros seleccionados.
          </div>
        ) : (
          <ul className="grid gap-2">
            {filtered.map((it) => (
              <UploadRow key={it.file_id} item={it} onDelete={canDelete ? remove : null} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function UploadRow({ item, onDelete }) {
  const isImg = (item.mime || "").startsWith("image/");
  return (
    <li
      className="flex items-center gap-3 bg-white border border-stone-200 rounded px-3 py-2"
      data-testid={`uploads-row-${item.file_id}`}
    >
      <div className="w-12 h-12 rounded overflow-hidden bg-stone-100 flex items-center justify-center shrink-0">
        {isImg ? (
          <AuthImg fileId={item.file_id} className="w-full h-full object-cover" />
        ) : (
          <FileText className="w-6 h-6 text-stone-400" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <a
          href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/files/${item.file_id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm font-medium text-stone-800 hover:underline truncate block"
          data-testid={`uploads-link-${item.file_id}`}
        >
          {item.filename}
        </a>
        <div className="text-xs text-stone-500 truncate">
          <span className="uppercase tracking-wider mr-2">{item.doc_type}</span>
          {item.phase && <span className="mr-2">· {item.phase}</span>}
          {item.caption && <span className="mr-2">· {item.caption}</span>}
          <span className="text-stone-400">· {humanSize(item.size_bytes)} · {fmtDate(item.created_at)}</span>
        </div>
      </div>
      {onDelete && (
        <button
          onClick={() => onDelete(item.file_id)}
          className="text-stone-400 hover:text-red-700 p-1"
          aria-label="Eliminar"
          data-testid={`uploads-delete-${item.file_id}`}
        >
          <Trash className="w-4 h-4" />
        </button>
      )}
    </li>
  );
}
